import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Table, Button, Upload, message, Space, Modal, List } from "antd";
import { UploadOutlined, DownloadOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  importTemplateApi,
  downloadAmbulanceTemplateApi,
  downloadCrewmemberTemplateApi,
  downloadParkingBayTemplateApi,
  downloadShiftTemplateApi,
} from "../apis/bulkUpload";
import { showNotification } from "../redux/actions/notification";

const dataTypes = [
  // { key: "ambulance", label: "Ambulance Data" },
  { key: "crew", label: "Crew Member Data" },
  // { key: "parking", label: "Parking Bay Data" },
  // { key: "shift", label: "Shift Data" },
];

const BulkUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState({});
  const [summaryModal, setSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  const dispatch = useDispatch();

  const handleFileChange = (file, dataTypeKey) => {
    setSelectedFiles((prev) => ({ ...prev, [dataTypeKey]: file }));
    return false;
  };

  const handleRemoveFile = (dataTypeKey) => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[dataTypeKey];
      return newFiles;
    });
  };

  const handleUpload = async (dataTypeKey) => {
    const file = selectedFiles[dataTypeKey];
    if (!file) {
      message.warning("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("sheet", file);

    try {
      let response;

      switch (dataTypeKey) {
        case "crew":
          response = await importTemplateApi("/employee", formData);
          break;

        case "ambulance":
          response = await importTemplateApi("/ambulance", formData);
          break;

        case "parking":
          response = await importTemplateApi("/parking", formData);
          break;

        case "shift":
          response = await importTemplateApi("/shift", formData);
          break;

        default:
          message.error("Invalid upload category.");
          return;
      }

      // API success return
      const result = response?.object;

      // Show summary modal if employees upload
      if (dataTypeKey === "crew" && result) {
        setSummaryData(result);
        setSummaryModal(true);
      }

      dispatch(
        showNotification({
          type: "success",
          message: "Success",
          description: "File uploaded successfully",
        })
      );

      handleRemoveFile(dataTypeKey);
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          message: "Unable to Upload the File",
          description: error?.message,
        })
      );
    }
  };

  const handleDownloadTemplate = async (dataTypeKey) => {
    try {
      let response;
      let fileName = "";

      switch (dataTypeKey) {
        case "ambulance":
          response = await downloadAmbulanceTemplateApi();
          fileName = "ambulance_template.xlsx";
          break;

        case "crew":
          response = await downloadCrewmemberTemplateApi();
          fileName = "crew_template.xlsx";
          break;

        case "parking":
          response = await downloadParkingBayTemplateApi();
          fileName = "parking_template.xlsx";
          break;

        case "shift":
          response = await downloadShiftTemplateApi();
          fileName = "shift_template.xlsx";
          break;

        default:
          message.error("Unknown template type.");
          return;
      }

      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link.href);

      dispatch(
        showNotification({
          type: "success",
          message: "Success",
          description: "File Downloaded successfully",
        })
      );
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          message: "Unable to Download the File",
          description: error?.message,
        })
      );
    }
  };
  const downloadFailedIdsAsCSV = () => {
    if (!summaryData?.failedEmployeeCodes?.length) {
      message.warning("No failed IDs available to download.");
      return;
    }
  
    const headers = "Failed Employee Codes\n";
    const csvContent =
      headers + summaryData.failedEmployeeCodes.join("\n");
  
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
  
    const link = document.createElement("a");
    link.href = url;
    link.download = "failed_employee_codes.csv";
    link.click();
  
    window.URL.revokeObjectURL(url);
  };
  
  const columns = [
    {
      title: "Data Type",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Download Template",
      key: "download",
      render: (_, record) => (
        <Button icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate(record.key)}>
          Download
        </Button>
      ),
    },
    {
      title: "Select File",
      key: "select",
      render: (_, record) => (
        <Space direction="vertical">
          <Upload beforeUpload={(file) => handleFileChange(file, record.key)} showUploadList={false}>
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>

          {selectedFiles[record.key] && (
            <div>
              <span>{selectedFiles[record.key].name}</span>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveFile(record.key)}
              >
                Remove
              </Button>
            </div>
          )}
        </Space>
      ),
    },
    {
      title: "Upload",
      key: "upload",
      render: (_, record) => (
        <Button
          type="primary"
          disabled={!selectedFiles[record.key]}
          onClick={() => handleUpload(record.key)}
        >
          Upload
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2>Bulk Upload</h2>

      <Table columns={columns} dataSource={dataTypes} pagination={false} rowKey="key" />

      {/* Summary Modal */}
      <Modal
        title="Import Summary"
        open={summaryModal}
        onCancel={() => setSummaryModal(false)}
        closable={false}
        maskClosable={false}
        footer={[
          summaryData?.failedEmployeeCodes?.length > 0 && (
            <Button key="download" onClick={downloadFailedIdsAsCSV}>
              Download Failed IDs
            </Button>
          ),
          <Button key="close" type="primary" onClick={() => setSummaryModal(false)}>
            Close
          </Button>,
        ]}        
      >
        {summaryData ? (
          <div>
            <p><b>Total Employees:</b> {summaryData.totalEmployees} | <b>Successful:</b> {summaryData.successfulEmployees}</p>
            <p><b>Failed Employee Code:</b> {summaryData.failedEmployeeCodes?.length || 0}</p>

            {summaryData.failedEmployeeCodes?.length > 0 && (
              <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #eee", padding: 10 }}>
                <List
                  dataSource={summaryData.failedEmployeeCodes}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default BulkUpload;
